// js
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { AppHeader, AppFooter, LoadingScreen } from '@its-svp/svp-common-ui';
import { ToastContainer } from 'react-toastify';
import { getUser, updateUser } from '../actions/userActions';
import { setSplunkLearningLink } from '../actions/stringActions';
import WhatsNewModalContent from '../components/WhatsNewModalContent';
import languages from '../constants/languages';
import { get } from 'lodash';
import splunkLogger, { initializeLogger } from '../utils/splunkClientLogger';

// This is a class-based component because the current
// version of hot reloading won't hot reload a stateless
// component at the top-level.
class App extends React.Component {
  constructor(props) {
    super(props);
    this.onUpdateUserLang = this.onUpdateUserLang.bind(this);
    this.svpEventListener = this.svpEventListener.bind(this);
  }

  componentDidMount() {
    this.props.refreshUser();
    this.initializeLoggerIfAuthenticated(this.props.currentUser);

    ['click', 'keypress'].forEach(event => {
      window.addEventListener(event, this.svpEventListener);
    });
  }

  componentWillUnmount() {
    ['click', 'keypress'].forEach(event => {
      window.removeEventListener(event, this.svpEventListener);
    });
  }

  componentWillReceiveProps(nextProps){
    const { location, currentUser, setSplunkLearningLink } = this.props;
    const userChanged = nextProps.currentUser !== currentUser;
    const pathChanged = nextProps.location.pathname !== location.pathname;

    if (userChanged || pathChanged) {
      setSplunkLearningLink();
    }

    if (!nextProps.currentUser.loading) {
      this.initializeLoggerIfAuthenticated(nextProps.currentUser);
      // this check will always be evaluated if currentUser.loading == false, similar to the other services
      if (!nextProps.currentUser.logged_in) {
        window.location.replace('/svp/login');
      } else {
        // only do these checks if the user transitioned from loading = true > false
        if (currentUser.loading) {
          // only redirect if user is at root path
          if (window.location.pathname === '/') {
            if (nextProps.currentUser.type === 'internal') {
              window.location.assign('/welcome');
            } else if (nextProps.currentUser.type.startsWith('partner')) {
              window.location.replace('/opportunity');
            } else {
              browserHistory.push('/coe');
            }
          }
          //account view --> account-details redirect
          let accountListRegex = /\/account\/(\d+|[a-zA-Z0-9]{15}|[a-zA-Z0-9]{18})\/*$/;
          let accountListPath = RegExp(accountListRegex);
          if (nextProps.currentUser.type === 'internal' && accountListPath.test(window.location.pathname)) {
            window.location.assign(`/account-details/${window.location.pathname.match(accountListRegex)[1]}`);
          }
        }
      }
    }
  }

  initializeLoggerIfAuthenticated(currentUser) {
    if (currentUser && !currentUser.loading && currentUser.logged_in) {
      try {
        initializeLogger();
      } catch (error) {
        // Silent fallback - logger will continue with stub methods
      }
    }
  }

  svpEventListener(e) {
    if (e.type === 'click' || e.keyCode === 13) {
      let elementPath = e.composedPath();
      let element = elementPath[1] ? elementPath[1].closest('[data-svp]') : null;
      if (e.target.dataset.svp || element) {
        const objectifyAttributes = (attributes) => {
          let returnObj = {};
          attributes.forEach((attr) => {
            returnObj[attr.name] = attr.value;
          });
          return returnObj;
        };

        let loggedElement = {
          target: {
            outerHTML: e.target['outerHTML'],
            innerText: e.target.innerText,
            classList: Object.values(e.target.classList),
            dataset: e.target.dataset,
            nodeName: e.target.nodeName,
            attributes: objectifyAttributes(Object.values(e.target.attributes))
          },
          parent: {
            innerText: get(elementPath, '[1].innerText'),
            classList: Object.values(get(elementPath, '[1].classList')),
            dataset: get(elementPath, '[1].dataset'),
            nodeName: get(elementPath, '[1].nodeName'),
            attributes: objectifyAttributes(Object.values(get(elementPath, '[1].attributes')))
          },
          eventType: e.type === 'click' ? 'click' : 'enter'
        };
        if (element) {
          loggedElement['closest-svp-parent'] = {
            innerText: element.innerText,
            classList: element.classList,
            dataset: element.dataset,
            nodeName: element.nodeName,
            attributes: objectifyAttributes(Object.values(element.attributes))
          };
        }
        splunkLogger.info('clickOrEnterEvent', loggedElement);
      }
    }
  }

  onUpdateUserLang(lang) {
    const { updateUser } = this.props;

    updateUser({
      lang: lang
    });
  }

  render() {
    const { currentUser, strings } = this.props;
    if (!currentUser.loading && currentUser.logged_in) {
      let envName = '';

      // process.env.NODE_ENV is loaded in the webpack config
      if (process.env.NODE_ENV === 'development') {
        envName = 'dev';
      }

      return (
        <div className='app-layout'>
          <div className='svp-common-ui'>
            <AppHeader
              WhatsNewContent={<WhatsNewModalContent />}
              env={{ name: envName }}
              currentUser={currentUser}
              strings={strings}
              languageArray={languages}
              langSwitcherCallback={this.onUpdateUserLang}
            />
          </div>
          <ToastContainer />
          <div className='app-body'>
            <div className='app-content'>
              {this.props.children}
            </div>
            <div className='svp-common-ui'>
              <AppFooter strings={strings} />
            </div>
          </div>
        </div>
      );
    } else {
      return <div className='svp-common-ui'><LoadingScreen /></div>;
    }
  }
}

App.propTypes = {
  children: PropTypes.element,
  strings: PropTypes.object,
  currentUser: PropTypes.object,
  refreshUser: PropTypes.func,
  location: PropTypes.object,
  setSplunkLearningLink: PropTypes.func,
  updateUser: PropTypes.func
};

function mapStateToProps(state) {

  return {
    strings: state.strings,
    currentUser: state.user
  };
}

const mapDispatchToProps = (dispatch) => {

  return {
    refreshUser: () => {
      dispatch(getUser());
    },
    signOut: () => {
      window.location = '/api/v1/svp/logout';
    },
    setSplunkLearningLink: (type) => {
      dispatch(setSplunkLearningLink(type));
    },
    updateUser: (user) => {
      dispatch(updateUser(user));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);



// WEBPACK FOOTER //
// ./src/containers/AppLayout.js